/**
 * useSEO.js
 *
 * Petit utilitaire SEO “SPA-friendly” : met à jour le <title>, la meta description,
 * la canonical, et quelques tags OpenGraph/Twitter lorsque la route change.
 *
 * Pourquoi ?
 * - Dans une SPA, les meta-tags ne changent pas automatiquement par page.
 * - Ce hook permet d'avoir un minimum de cohérence SEO et social sharing.
 *
 * Limites :
 * - Certains crawlers n'exécutent pas ou partiellement le JS.
 * - Pour un SEO maximal, il faut du SSR (Next.js) ou du pré-render.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_SITE_URL = "https://how-much.app";

function upsertMetaByName(name, content) {
  if (!content) return;

  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function upsertMetaByProperty(property, content) {
  if (!content) return;

  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function setCanonical(href) {
  if (!href) return;

  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

/**
 * useSEO
 * @param {Object} params
 * @param {string} params.title - Titre de la page
 * @param {string} params.description - Description de la page (meta description)
 * @param {string} [params.canonicalPath] - Chemin canonique (ex: "/blog")
 * @param {string} [params.ogType="website"] - Type OG (website|article)
 * @param {string} [params.siteUrl] - URL du site (override si besoin)
 */
export function useSEO({ title, description, canonicalPath, ogType = "website", siteUrl }) {
  const location = useLocation();
  const baseUrl = siteUrl || process.env.REACT_APP_SITE_URL || DEFAULT_SITE_URL;

  useEffect(() => {
    const path = canonicalPath || location.pathname;
    const canonicalUrl = `${baseUrl}${path.startsWith("/") ? path : `/${path}`}`;

    if (title) document.title = title;

    upsertMetaByName("description", description);
    upsertMetaByName("robots", "index,follow");

    setCanonical(canonicalUrl);

    // OpenGraph (social sharing)
    upsertMetaByProperty("og:title", title);
    upsertMetaByProperty("og:description", description);
    upsertMetaByProperty("og:url", canonicalUrl);
    upsertMetaByProperty("og:type", ogType);

    // Twitter (fallback)
    upsertMetaByName("twitter:title", title);
    upsertMetaByName("twitter:description", description);
  }, [title, description, canonicalPath, ogType, baseUrl, location.pathname]);
}
